import { Ban, Copy, Plus, RefreshCw, Terminal, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { FilterChips, JobStatusBadge, Pagination } from '../components/ui/data';
import { Drawer, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  Mono,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui/primitives';
import {
  useDeleteJob,
  useJob,
  useJobAction,
  useJobLogs,
  useJobs,
  useProject,
  useSession,
  useSearchQueries,
} from '../hooks/queries';
import { formatDateTime, formatNumber, formatRelative, titleCase, truncate } from '../lib/utils';
import { JOB_TYPES, type JobType } from '../types/api';
import { LaunchAgentModal } from '../components/jobs/LaunchAgentModal';
import { agentOptionsFor, agentRunPrompt } from '../lib/agent-jobs';

const STATUS_CHIPS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'CLAIMED', label: 'Running' },
  { value: 'DONE', label: 'Done' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const JobsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const type = JOB_TYPES.find((value) => value === typeParam);
  const [page, setPage] = useState(1);
  const [launchType, setLaunchType] = useState<JobType | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Polling here is worth it: this page is what you watch while agents are working.
  const { data, isLoading, error, refetch } = useJobs(projectId, { status, type, page, pageSize: 25 }, 10_000);
  const { data: project } = useProject(projectId);
  const { data: session } = useSession();
  const { data: searches } = useSearchQueries(project?.kind === 'OUTREACH' ? projectId : undefined);
  useEffect(() => { setPage(1); setCreateOpen(false); }, [projectId]);
  const jobAction = useJobAction();
  const deleteJob = useDeleteJob();
  const toast = useToast();

  const jobs = data?.items ?? [];
  // The backend knows its public endpoint, including when the dashboard uses a different port.
  const mcpUrl = session?.config.mcpEndpoint ?? `${window.location.origin}/mcp`;

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Jobs"
        description="Choose a task, queue the brief, and start your connected agent. Follow its progress here."
        actions={
          <>
            <Button icon={<RefreshCw className="h-4 w-4" />} onClick={() => void refetch()}>
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => { setLaunchType(undefined); setCreateOpen(true); }}
            >
              Launch agent
            </Button>
          </>
        }
      />

      <div className={`mb-4 grid gap-3 sm:grid-cols-2 ${project?.kind === 'CONTENT' ? '' : 'lg:grid-cols-4'}`}>
        {project ? agentOptionsFor(project.kind).map((option) => (
          <button type="button" key={option.type} className="rounded-xl border border-line bg-surface p-4 text-left shadow-card transition-colors hover:border-brand/40 hover:bg-brand/5"
            onClick={() => { setLaunchType(option.type); setCreateOpen(true); }}>
            <p className="text-sm font-semibold text-ink">{option.label}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted">{option.description}</p>
          </button>
        )) : null}
      </div>

      <Card className="mb-4 p-4">
        <details>
          <summary className="cursor-pointer text-[13px] font-medium text-ink"><Terminal className="mr-2 inline h-4 w-4 text-faint" />Connect an agent & start working</summary>
          <div className="mt-3 space-y-3 text-[13px] text-muted">
            <p>Jobs wait until a connected agent claims them. Connect once, then paste the run prompt to start a session.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><p className="text-xs font-medium">Claude Code</p><pre className="mt-1 overflow-x-auto rounded-lg bg-raised p-3 text-[11px]">{`claude mcp add --transport http rentx ${mcpUrl}`}</pre></div>
              <div><p className="text-xs font-medium">Codex</p><pre className="mt-1 overflow-x-auto rounded-lg bg-raised p-3 text-[11px]">{`codex mcp add rentx --url ${mcpUrl}`}</pre></div>
            </div>
            <pre className="whitespace-pre-wrap break-words rounded-lg bg-raised p-3 font-mono text-xs">{agentRunPrompt(projectId, type)}</pre>
            <Button size="sm" icon={<Copy className="h-3.5 w-3.5" />} onClick={async () => {
              try { await navigator.clipboard.writeText(agentRunPrompt(projectId, type)); toast.success('Agent prompt copied'); }
              catch { toast.error('Could not copy. Select and copy the prompt above.'); }
            }}>Copy {type ? titleCase(type).toLowerCase() : 'all jobs'} prompt</Button>
          </div>
        </details>
      </Card>

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <FilterChips options={STATUS_CHIPS} value={status} onChange={(next) => { setStatus(next); setPage(1); }} />
          <Select aria-label="Filter job type" className="ml-auto w-auto" value={type ?? ''} onChange={(event) => {
            const params = new URLSearchParams(searchParams);
            event.target.value ? params.set('type', event.target.value) : params.delete('type');
            setSearchParams(params, { replace: true }); setPage(1);
          }}><option value="">All agent types</option>{JOB_TYPES.map((value) => <option key={value} value={value}>{titleCase(value)}</option>)}</Select>
        </div>

        {error ? <div className="p-4"><ErrorBlock error={error} /></div> : jobs.length === 0 ? (
          <EmptyState
            title={status || type ? 'No matching jobs' : 'Ready for your first agent'}
            description={status || type ? 'Try another status or agent type.' : 'Choose a task above to prepare a job for your agent.'}
            action={status || type ? <Button onClick={() => { setStatus(undefined); setSearchParams({}); setPage(1); }}>Clear filters</Button> : <Button variant="primary" onClick={() => { setLaunchType(undefined); setCreateOpen(true); }}>Launch agent</Button>}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Context</Th>
                <Th className="text-right">Found</Th>
                <Th className="text-right">Created</Th>
                <Th className="text-right">Skipped</Th>
                <Th>Worker</Th>
                <Th>Age</Th>
                <Th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <Tr key={job.id}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => setOpenId(job.id)}
                      className="font-medium text-ink hover:text-brand"
                    >
                      {titleCase(job.type)}
                    </button>
                  </Td>
                  <Td>
                    <JobStatusBadge status={job.status} />
                    {job.attempts > 1 ? (
                      <span className="ml-1.5 text-2xs text-faint">
                        attempt {job.attempts}/{job.maxAttempts}
                      </span>
                    ) : null}
                  </Td>
                  <Td className="text-muted">
                    {job.leadType ? (
                      <Badge dot={job.leadType.color}>{truncate(job.leadType.name, 22)}</Badge>
                    ) : job.campaign ? (
                      <Badge tone="brand">{truncate(job.campaign.name, 22)}</Badge>
                    ) : job.contentBatch ? (
                      <Badge tone="brand">{truncate(job.contentBatch.name, 22)}</Badge>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className="text-right tabular-nums text-muted">{job.itemsFound || '—'}</Td>
                  <Td className="text-right tabular-nums text-positive">
                    {job.itemsCreated || '—'}
                  </Td>
                  <Td className="text-right tabular-nums text-faint">{job.itemsSkipped || '—'}</Td>
                  <Td className="text-faint">
                    {job.claimedBy ? <Mono>{truncate(job.claimedBy, 18)}</Mono> : '—'}
                  </Td>
                  <Td className="whitespace-nowrap text-faint">{formatRelative(job.createdAt)}</Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      {job.status === 'FAILED' || job.status === 'CANCELLED' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await jobAction.mutateAsync({ id: job.id, action: 'retry' });
                              toast.success('Job requeued');
                            } catch (error) {
                              toast.error(error);
                            }
                          }}
                        >
                          Retry
                        </Button>
                      ) : null}
                      {job.status === 'PENDING' || job.status === 'CLAIMED' ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Cancel"
                          onClick={async () => {
                            try {
                              await jobAction.mutateAsync({ id: job.id, action: 'cancel' });
                              toast.success('Job cancelled');
                            } catch (error) {
                              toast.error(error);
                            }
                          }}
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete"
                          onClick={async () => {
                            try {
                              await deleteJob.mutateAsync(job.id);
                              toast.success('Job deleted');
                            } catch (error) {
                              toast.error(error);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-danger" />
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
        {data && data.total > 0 ? <Pagination page={data.page} totalPages={data.totalPages} total={data.total} pageSize={data.pageSize} onPage={setPage} /> : null}
      </Card>

      {project?.kind === 'OUTREACH' ? <Card className="mt-4">
        <div className="border-b border-line px-5 py-4">
          <h2 className="text-[15px] font-semibold text-ink">Search coverage</h2>
          <p className="mt-0.5 text-[13px] text-muted">
            Every query an agent has run. Agents read this before searching, which is what stops
            them from covering the same ground twice.
          </p>
        </div>
        {searches?.length ? (
          <Table>
            <thead>
              <tr>
                <Th>Query</Th>
                <Th>Source</Th>
                <Th className="text-right">Results</Th>
                <Th className="text-right">New leads</Th>
                <Th>Notes</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {searches.slice(0, 40).map((search) => (
                <Tr key={search.id}>
                  <Td className="font-medium text-ink">{truncate(search.query, 60)}</Td>
                  <Td className="text-muted">{search.source ?? '—'}</Td>
                  <Td className="text-right tabular-nums text-muted">{search.resultsFound}</Td>
                  <Td className="text-right tabular-nums text-positive">{search.newLeads}</Td>
                  <Td className="text-muted">{search.notes ? truncate(search.notes, 60) : '—'}</Td>
                  <Td className="whitespace-nowrap text-faint">{formatRelative(search.createdAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            title="No searches recorded yet"
            description="Research agents log each query they run, along with how many new leads it produced."
          />
        )}
      </Card> : null}

      <JobDrawer jobId={openId} onClose={() => setOpenId(null)} />

      <LaunchAgentModal key={projectId} open={createOpen} onClose={() => setCreateOpen(false)} projectId={projectId} initialType={launchType} />
    </Page>
  );
};

const LEVEL_TONES: Record<string, string> = {
  DEBUG: 'text-faint',
  INFO: 'text-muted',
  WARN: 'text-warning',
  ERROR: 'text-danger',
};

const JobDrawer = ({ jobId, onClose }: { jobId: string | null; onClose: () => void }) => {
  const { data: job } = useJob(jobId ?? undefined, 5000);
  const { data: logs } = useJobLogs(jobId ?? undefined, 5000);

  const payload =
    job && typeof job.payload === 'string' ? safeParse(job.payload) : (job?.payload ?? {});
  const result = job?.result ? safeParse(job.result) : null;

  return (
    <Drawer
      open={Boolean(jobId)}
      onClose={onClose}
      width="lg"
      title={job ? `${titleCase(job.type)} job` : 'Job'}
      subtitle={
        job ? (
          <span className="flex flex-wrap items-center gap-2">
            <JobStatusBadge status={job.status} />
            {job.leadType ? <Badge>{job.leadType.name}</Badge> : null}
            {job.contentBatch ? <Badge>{job.contentBatch.name}</Badge> : null}
            {job.claimedBy ? <Mono>{job.claimedBy}</Mono> : null}
          </span>
        ) : null
      }
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {!job ? (
        <div className="p-5 text-[13px] text-faint">Loading…</div>
      ) : (
        <div className="divide-y divide-line">
          <div className="grid grid-cols-3 gap-3 px-5 py-4">
            {[
              ['Found', job.itemsFound],
              ['Created', job.itemsCreated],
              ['Skipped', job.itemsSkipped],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <div className="text-2xs uppercase tracking-wide text-faint">{label}</div>
                <div className="text-lg font-semibold tabular-nums text-ink">
                  {formatNumber(Number(value))}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 py-4">
            <span className="field-label">Timing</span>
            <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
              {[
                ['Created', formatDateTime(job.createdAt)],
                ['Claimed', formatDateTime(job.claimedAt)],
                ['Last heartbeat', formatDateTime(job.heartbeatAt)],
                ['Lease expires', formatDateTime(job.leaseExpiresAt)],
                ['Finished', formatDateTime(job.finishedAt)],
                ['Attempts', `${job.attempts} / ${job.maxAttempts}`],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-2">
                  <dt className="text-faint">{label}</dt>
                  <dd className="text-ink">{value}</dd>
                </div>
              ))}
            </dl>
            {job.error ? (
              <p className="mt-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
                {job.error}
              </p>
            ) : null}
          </div>

          <div className="px-5 py-4">
            <span className="field-label">Payload</span>
            <pre className="overflow-x-auto rounded-lg bg-raised px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </div>

          {result ? (
            <div className="px-5 py-4">
              <span className="field-label">Result</span>
              <pre className="overflow-x-auto rounded-lg bg-raised px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          ) : null}

          <div className="px-5 py-4">
            <span className="field-label">Log ({logs?.length ?? 0})</span>
            <div className="space-y-1">
              {(logs ?? []).map((log) => (
                <div key={log.id} className="flex gap-2 text-xs leading-relaxed">
                  <span className="shrink-0 tabular-nums text-faint">
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </span>
                  <span className={`shrink-0 font-medium ${LEVEL_TONES[log.level] ?? 'text-muted'}`}>
                    {log.level}
                  </span>
                  <span className="min-w-0 text-ink">{log.message}</span>
                </div>
              ))}
              {!logs?.length ? <p className="text-[13px] text-faint">No log lines yet.</p> : null}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

const safeParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};
